import * as Sentry from '@sentry/react'

const CHUNK_RELOAD_KEY = 'pb:chunk-reload'

const DYNAMIC_IMPORT_ERROR_MATCHERS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Failed to load module script',
  'ChunkLoadError',
  'Loading chunk',
]

export interface DynamicImportFailureDetails {
  message: string
  failedModuleUrl: string | null
}

export interface DynamicImportHandlingResult extends DynamicImportFailureDetails {
  isDynamicImportFailure: boolean
  reloadAttempted: boolean
}

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  if (typeof (error as { message?: unknown } | null)?.message === 'string') {
    return String((error as { message?: unknown }).message)
  }
  return ''
}

export const getDynamicImportFailureDetails = (
  error: unknown
): DynamicImportFailureDetails => {
  const message = getErrorMessage(error)
  const failedModuleUrlMatch = message.match(
    /(https?:\/\/[^\s)]+?\.m?js\b[^\s)]*)|(\/assets\/[^\s)]+?\.m?js\b[^\s)]*)/
  )

  return {
    message,
    failedModuleUrl: failedModuleUrlMatch?.[0] || null,
  }
}

export const isDynamicImportFailure = (error: unknown): boolean => {
  const { message } = getDynamicImportFailureDetails(error)
  if (!message) return false
  return DYNAMIC_IMPORT_ERROR_MATCHERS.some((matcher) =>
    message.includes(matcher)
  )
}

const hasTriedChunkReload = (): boolean => {
  if (typeof window === 'undefined') return false
  return window.sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1'
}

const markChunkReloadTried = () => {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
}

const reportDynamicImportFailure = (
  details: DynamicImportFailureDetails,
  source: string,
  reloadAttempted: boolean
) => {
  Sentry.withScope((scope) => {
    scope.setTag('error_kind', 'dynamic_import_failure')
    scope.setTag('dynamic_import_source', source)
    scope.setTag('dynamic_import_reload_attempted', String(reloadAttempted))
    if (details.failedModuleUrl) {
      scope.setTag('failed_module_url', details.failedModuleUrl)
    }
    scope.setContext('dynamic_import_failure', {
      source,
      message: details.message || null,
      failedModuleUrl: details.failedModuleUrl,
      reloadAttempted,
    })
    Sentry.captureMessage('Dynamic import failure detected', 'error')
  })
}

export const handleDynamicImportFailure = (
  error: unknown,
  source: string
): DynamicImportHandlingResult => {
  const details = getDynamicImportFailureDetails(error)
  const isFailure = isDynamicImportFailure(error)

  if (!isFailure) {
    return {
      ...details,
      isDynamicImportFailure: false,
      reloadAttempted: false,
    }
  }

  const reloadAttempted = !hasTriedChunkReload()
  reportDynamicImportFailure(details, source, reloadAttempted)

  if (reloadAttempted) {
    markChunkReloadTried()
    try {
      window.location.reload()
    } catch {
      // Ignore reload failures and let the current error surface.
    }
  }

  return {
    ...details,
    isDynamicImportFailure: true,
    reloadAttempted,
  }
}
